import React, { useState } from "react"
import { ActivityIndicator, StyleSheet, Text, View } from "react-native"
import { theme } from "@common/theme/theme"
import FunnelChoiceChip from "./FunnelChoiceChip"

/**
 * Les matières d'un compositeur, cochables sur l'écran, sur des lignes qui
 * passent à la suivante. Jamais sur un rail horizontal : au téléphone, un
 * doigt posé dessus pour descendre la page faisait défiler le rail, et la
 * page ne bougeait pas — un parent s'y est cru bloqué.
 *
 * Deux paliers, pour que la question suivante reste près : d'abord celles que
 * le produit met en avant (il a un programme pour elles dans cette classe),
 * puis « + N autres matières » qui déplie le reste — au lycée, la liste
 * proposable dépasse
 * trente matières, les huit langues et toutes les spécialités technologiques
 * comprises. Rien n'est caché pour autant : tout se coche ici, sans modale.
 * Le pli s'ouvre de lui-même si une matière déjà choisie s'y trouve
 * (reprise), et ne se referme pas tant qu'il en porte une. S'il n'y a rien à
 * mettre en avant, tout se montre.
 */
export const splitSubjectTiers = (options, selectedIds) => {
    const primary = options.filter((o) => !o.secondary)
    if (primary.length === 0) return { primary: options, secondary: [] }
    const secondary = options
        .filter((o) => o.secondary)
        .sort((a, b) => a.label.localeCompare(b.label, "fr"))
    return {
        primary,
        secondary,
        secondarySelected: secondary.some((o) => selectedIds.includes(o.id)),
    }
}

const FunnelSubjectChips = ({
    options = [],
    selectedIds = [],
    maxSubjects,
    onToggle,
    loading = false,
    emptyText,
}) => {
    const [expanded, setExpanded] = useState(false)
    const { primary, secondary, secondarySelected } = splitSubjectTiers(
        options,
        selectedIds
    )
    const open = expanded || secondarySelected

    if (loading && options.length === 0) {
        return (
            <View style={styles.state}>
                <ActivityIndicator color={theme.colors.primary} />
            </View>
        )
    }
    if (options.length === 0) {
        return emptyText ? (
            <View style={styles.state}>
                <Text style={styles.emptyText}>{emptyText}</Text>
            </View>
        ) : null
    }

    const chip = (option) => {
        const selected = selectedIds.includes(option.id)
        // Au plafond, la chip en trop cesse d'être cochable : le nombre n'est
        // jamais annoncé.
        const locked =
            !selected &&
            maxSubjects != null &&
            selectedIds.length >= maxSubjects
        return (
            <FunnelChoiceChip
                key={option.id}
                label={option.label}
                iconUri={option.iconUri}
                selected={selected}
                locked={locked}
                onPress={() => onToggle?.(option.id)}
            />
        )
    }

    return (
        <View style={styles.chips}>
            {primary.map(chip)}
            {secondary.length > 0 && !open ? (
                <FunnelChoiceChip
                    ghost
                    label={`+ ${secondary.length} autres matières`}
                    onPress={() => setExpanded(true)}
                />
            ) : null}
            {open ? secondary.map(chip) : null}
            {open && !secondarySelected ? (
                <FunnelChoiceChip
                    ghost
                    label="Réduire"
                    onPress={() => setExpanded(false)}
                />
            ) : null}
        </View>
    )
}

const styles = StyleSheet.create({
    chips: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
    state: { minHeight: 40, justifyContent: "center" },
    emptyText: {
        fontFamily: "Montserrat-Medium",
        fontSize: 13,
        color: theme.colors.grey,
    },
})

export default FunnelSubjectChips
